import { useCallback, useEffect, useReducer } from 'react';

/** En skærm i navigations-stacken. `type` afgør hvilken komponent der renderes. */
export interface Screen {
  type: string;
  [key: string]: unknown;
}

export type NavAction =
  | { type: 'push'; screen: Screen }
  | { type: 'pop' }
  | { type: 'replace'; stack: Screen[] };

/** Ren reducer — testbar uden DOM. */
export function navReducer(stack: Screen[], action: NavAction): Screen[] {
  switch (action.type) {
    case 'push':
      return [...stack, action.screen];
    case 'pop':
      return stack.length > 1 ? stack.slice(0, -1) : stack;
    case 'replace':
      return action.stack.length > 0 ? action.stack : stack;
    default:
      return stack;
  }
}

export interface NavStack {
  stack: Screen[];
  current: Screen;
  push: (screen: Screen) => void;
  back: () => void;
  replaceStack: (stack: Screen[]) => void;
}

/**
 * Stack-navigation koblet på history, så Androids OS-back popper stacken
 * i stedet for at lukke webview'et. In-app back kalder history.back(), og
 * popstate-handleren udfører selve pop'et (single source of truth).
 */
export function useNavStack(initial: Screen): NavStack {
  const [stack, dispatch] = useReducer(navReducer, [initial]);

  const push = useCallback((screen: Screen) => {
    dispatch({ type: 'push', screen });
    window.history.pushState({ portal: true }, '');
    window.scrollTo(0, 0);
  }, []);

  const back = useCallback(() => {
    if (window.history.state?.portal) window.history.back();
    else dispatch({ type: 'pop' });
  }, []);

  const replaceStack = useCallback((next: Screen[]) => {
    dispatch({ type: 'replace', stack: next });
  }, []);

  useEffect(() => {
    const onPop = () => dispatch({ type: 'pop' });
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return { stack, current: stack[stack.length - 1], push, back, replaceStack };
}
